/**
 * The permission each screen belongs to (T-018, corrected for R-1).
 *
 * ✅ THESE ARE NOW THE REAL NAMES.
 *
 * They used to be guesses. Only 'product.index' and 'role.index' appeared
 * anywhere in this codebase — every sidebar item was gated on 'product.index',
 * which is what made the permission system decorative — and the rest were
 * derived from that pattern by writing `{resource}.index` and hoping.
 *
 * The backend's R-1 pass then found that ~100 of its OWN permission checks
 * named the wrong resource: sales orders were gated on `product.index`,
 * transactions on `measurement.index`, credits on `category.*`. So both sides
 * were wrong, in different ways, and they happened to be compatible. Every
 * check has since been renamed to its own resource and the full list published
 * in API_REFERENCE §16, which is what this file now mirrors.
 *
 * ⚠️ FIVE OF THE OLD GUESSES WERE WRONG, and they are the ones worth knowing:
 *
 *   was 'sales.index'       →  'salesOrder.index'
 *   was 'purchase.index'    →  'purchaseOrder.index'
 *   was 'subcategory.index' →  'subCategory.index'      (camelCase, not flat)
 *   was 'salestarget.index' →  'employeeTarget.index'
 *   credit ledger was       →  'creditTransaction.index' (it was on transaction.*)
 *
 * Nothing visibly changes today, because every user is a Super Admin holding
 * every permission. It starts mattering the moment the client creates a cashier
 * or a storekeeper role — and at that point a wrong name here hides a screen
 * from the person whose whole job it is. That is why this was fixed before the
 * roles exist rather than after somebody reports a missing menu.
 *
 * The unknown-resource rule in session.js still applies and is still the
 * backstop. But with real names it should now essentially never fire: every
 * resource below appears in the backend's permission data, so normal
 * deny-by-default is in force for all of them.
 */
export const ROUTE_PERMISSIONS = {
    '/product/list': 'product.index',
    '/product/create': 'product.index',
    // Its own permission rather than a slice of product.index, so a shop can
    // let a storekeeper print shelf labels without also letting them edit the
    // catalogue.
    //
    // ⚠️ WHAT THIS DOES AND DOES NOT DO. The labels are generated in the
    // browser from data already fetched under product.index — there is no
    // server call to refuse. So this decides who is OFFERED the feature; it
    // does not stop someone who can already read a code on screen from
    // encoding it. That is the right scope here (nothing secret is exposed),
    // but it must not be recorded anywhere as an access control it is not.
    // Real enforcement would mean a server-rendered PDF endpoint.
    '/product/qr/print': 'product.qr.print',

    '/user/role/list': 'role.index',
    '/user/role/create': 'role.index',

    '/user/list': 'user.index',
    '/user/create': 'user.index',
    // Its own permission, not a slice of user.* — API_REFERENCE §16 lists
    // `user.login.history` separately, and it is exactly the kind of thing a
    // shop would grant to an owner and not to a manager.
    '/user/login/history': 'user.login.history',

    '/bank/list': 'bank.index',
    '/bank/create': 'bank.index',

    '/supplier/list': 'supplier.index',
    '/supplier/create': 'supplier.index',

    '/warehouse/list': 'warehouse.index',
    '/warehouse/create': 'warehouse.index',

    '/measurement/list': 'measurement.index',
    '/measurement/create': 'measurement.index',

    '/category/list': 'category.index',
    '/category/create': 'category.index',

    // camelCase. The old flat 'subcategory.index' matched nothing.
    '/subcategory/list': 'subCategory.index',
    '/subcategory/create': 'subCategory.index',

    '/discount/list': 'discount.index',
    '/discount/create': 'discount.index',
    '/discount/product/list': 'discount.index',

    '/purchase/order/list': 'purchaseOrder.index',
    '/purchase/order/create': 'purchaseOrder.store',
    // Receiving is its own permission (`purchaseOrder.receive`) and deliberately
    // so: it is the act that CREATES STOCK. A storekeeper receives goods; a
    // buyer raises the order. They are different people in most shops and the
    // backend models them as different rights.
    '/purchase/order/recieve': 'purchaseOrder.receive',

    '/sales/order/list': 'salesOrder.index',
    '/sales/order/create': 'salesOrder.store',
    // The Sprint 3 rewrite of the same screen, live beside it behind a flag
    // (T-029). Whichever one the flag shows, it creates a sales order — a
    // rewrite is not a reason for a different answer to "may this person take
    // an order?". Both entries go when the old screen does.
    '/sales/order/new': 'salesOrder.store',

    // REQ-3 - editing an already-created invoice, while its delivery is not
    // yet complete and only on the fields the settings screen below allows.
    '/sales/order/:id/edit': 'salesOrder.update',

    // REQ-3, decision D-3 - which invoice fields an edit may touch. A super
    // admin's right, held apart from `salesOrder.update` (performing an
    // edit) the same way `/shop/settings` is held apart from selling.
    '/settings/sales-order/editable-fields': 'salesOrder.editSettings',

    // ── SALES REQUESTS (from erp-mobile) ────────────────────────────────────
    //
    // Two permissions, not one, and the split is the requirement: "if he has
    // the permission he can convert it into sales".
    //
    // `salesDraft.index` is reading the queue — an ordinary office right.
    // `salesDraft.convert` MOVES STOCK OUT OF THE GODOWN and creates customer
    // debt, in one irreversible transaction. It belongs to the same class as
    // `salesOrder.store`, and a shop that grants it alongside "can see the
    // list" has not understood what it does.
    // The queue itself. The conversion screen is a dynamic route and lives in
    // VIEW_SCREEN_PERMISSIONS below, gated on the narrower right.
    '/sales/request/list': 'salesDraft.index',

    '/transaction/list': 'transaction.index',
    '/transaction/credit/create': 'creditTransaction.store',
    // The credit LEDGER, which is expenses and income — a different resource
    // from the cash/bank journal, and gated separately by the backend. It was
    // on 'transaction.index' here, which would have shown the shop's expense
    // book to anyone allowed to see a customer payment.
    '/credit/list': 'creditTransaction.index',

    // BE-28. The Expenses section itself — an expense IS a credit
    // transaction, so it reuses creditTransaction.* rather than inventing a
    // parallel permission for the same underlying right.
    '/expense/create': 'creditTransaction.store',
    '/expense/list': 'creditTransaction.index',

    // BE-31 / BR-49. A second pair of eyes on a large expense — a
    // different right from posting one, and from managing the heads.
    '/expense/approvals': 'expense.approve',
    // The global auto-approval threshold. Super Admin has this
    // automatically; nobody else should even see the raw number.
    '/expense/setting': 'expenseSetting.update',

    // BE-30. Configuring a recurring rule (list+create gated the same as
    // every other master-data pair here) is a different right from
    // actually posting money against one, which the "Post Due Now" button
    // on the list screen itself checks via recurringExpense.post.
    '/expense/recurring/list': 'recurringExpense.index',
    '/expense/recurring/create': 'recurringExpense.store',

    // BE-27. Managing the HEADS (Rent, Utilities...) is a different right
    // from recording a payment against one. Both list and create gated on
    // '.index', same convention as every other master-data pair above
    // (category, subCategory, supplier, warehouse, measurement) — the
    // backend still enforces '.store'/'.update' separately on save.
    '/expense/category/list': 'creditCategory.index',
    '/expense/category/create': 'creditCategory.index',
    '/expense/subcategory/list': 'creditSubCategory.index',
    '/expense/subcategory/create': 'creditSubCategory.index',

    '/customer/list': 'customer.index',
    // Receivables ageing (BE-17). API_REFERENCE §11 states the requirement
    // explicitly: `customer.index`.
    '/customer/ageing': 'customer.index',
    // B-7. Setting a whole class of customer's credit terms moves the ceiling
    // for all of them at once, so it is not the same right as reading the list.
    '/customer/credit-policy': 'customerType.setCreditTerms',
    // A shop's commercial terms with its brands are not counter reading.
    '/supplier/commission': 'supplierCommission.index',

    '/employee/sales/target/list': 'employeeTarget.index',
    '/employee/sales/target/create': 'employeeTarget.store',
    '/employee/sales/target/user/list': 'employeeTarget.index',
    '/employee/sales/target/user/create': 'employeeTarget.store',

    // ---------------------------------------------------------------------
    // Screens that could not exist before this backlog was answered.
    // ---------------------------------------------------------------------

    // BE-01 / BE-08. `stock.index` is a NEW permission — nothing in the old
    // mapping could have named it, because nothing in the API exposed stock.
    '/stock/list': 'stock.index',
    '/stock/low': 'stock.index',

    // ⚠️ NOT `stock.index`. Counting the shelf is a storekeeper's job and
    // that right is granted widely; what the shelf COST is the shop's
    // buying position, and belongs with `comparison.profit` and
    // `report.profitLoss` on the short list.
    '/stock/valuation': 'stock.valuation',

    // B-5 and the stock-movement pack. Each route is gated on the right that
    // actually MOVES stock rather than on `stockAdjustment.index` — these
    // screens exist to file a document, and one that can only be read is a
    // dead end.
    //
    // ⚠️ Opening and wastage are deliberately different permissions even though
    // they are one screen. Restating a godown's opening position is a one-off
    // owner's act that nothing else reconciles against; recording breakage is a
    // daily storekeeper's act. A shop granting the second must not thereby have
    // granted the first.
    '/stock/opening': 'stockAdjustment.opening',
    '/stock/wastage': 'stockAdjustment.wastage',

    // Dispatching is what this screen needs to be useful at all. Receiving is a
    // separate grant, checked per row when the action is taken, because it is
    // performed by the person at the other end.
    '/stock/transfer': 'stockTransfer.store',

    '/purchase/return': 'purchaseReturn.store',

    // Bulk import. `import.store` and not `import.index`, because the screen
    // exists to load a file — and because its preview step, which looks like a
    // read, will report which product codes are already taken for any file
    // somebody cares to upload.
    '/import': 'import.store',

    // Filing a challan is what this screen is for, so `.store` is what opens
    // it. Cancelling one is a separate grant checked per row when the button is
    // pressed: it un-delivers goods that a printed document — possibly already
    // in a driver's hand — says went out, which is a correction to the record
    // rather than part of the daily loop.
    '/delivery/challan': 'deliveryChallan.store',

    // The owner's books. Deliberately two rights: a profit and loss statement
    // has to show cost of goods, which is the shop's buying position, while a
    // cashbook shows money in and out and is what whoever counts the drawer at
    // close of business needs.
    '/report/profit-loss': 'report.profitLoss',
    '/report/cashbook': 'report.cashbook',

    // ⚠️ NOT `report.profitLoss`. VAT and the two charges are deliberately
    // excluded from that statement — this report shows them instead, and it
    // discloses no cost of goods or margin, so it is cheap to grant on its own.
    '/report/charges': 'report.charges',

    // ⚠️ The audit trail, and `audit.index` is narrower than it looks. It
    // carries the before and after of every price, discount and cost in the
    // shop — a product's purchase_price history is readable there whether or
    // not the holder has `comparison.profit`. Granting it to "anyone who can
    // see reports" hands out the narrowest right in the system sideways.
    '/audit': 'audit.index',

    // BE-14. Both the list and the create screen; store is the one that moves
    // goods and money, so it is gated separately from merely looking.
    '/exchange/list': 'exchange.index',
    '/exchange/create': 'exchange.store',
    // Reading one exchange is the same right as reading the list, and the
    // endpoint behind it is gated identically. There is deliberately no
    // `exchange.show` on either side: a permission nobody has seeded hides the
    // screen from everyone, superadmin included — which is exactly how the QR
    // print screen stayed invisible to the whole shop.
    '/exchange/:id': 'exchange.index',

    // B-3. Clearing or bouncing a cheque moves a bank balance for real, and
    // bouncing one reverses an invoice's payment. `payment.settle` is not a
    // permission anyone should hold by accident.
    '/payment/pending': 'payment.settle',

    // BE-20. Comparison. `comparison.index` covers the screen; the shop axis
    // inside it additionally needs `comparison.cross_shop`, which the server
    // enforces and which the dimension control asks the server about rather
    // than assuming — a branch manager comparing their own months is routine,
    // a branch manager reading another branch's margins is the owner's call.
    '/comparison': 'comparison.index',

    // BE-03 / BE-16. Reading the shop list is `shop.index`; editing the invoice
    // header is `shop.update`, which is an owner's right, not a manager's.
    '/shop/settings': 'shop.update',

    // BE-19. Everyone's target progress. NOT `employeeTarget.index` — that
    // governs the target CATALOGUE, which is master data an administrator
    // maintains. This screen shows what named people earned towards their
    // bonus, which is a step from payroll, and the two rights are deliberately
    // separable: a storekeeper who may edit target templates has no business
    // reading the sales floor's performance.
    '/sales/targets': 'employeeTarget.overview',
}

/**
 * Screens every signed-in user reaches regardless of role.
 *
 * The dashboard is the landing page and the place RequireAuth sends anyone who
 * lacks a permission — gating it could strand a user in a redirect loop.
 *
 * The view screens (`/sales/order/:id`, `/customer/:id`, the return screen) are
 * left ungated on purpose: they are reached by clicking a row in a list that is
 * already gated, and a salesperson who can open the list can necessarily open
 * the row. Gating them again adds a second chance to get a key wrong for no
 * extra protection — the API is the real boundary either way.
 *
 * ⚠️ The return screen stays ungated here even though `salesOrder.return` now
 * exists and is checked server-side. That is deliberate and it is the same
 * argument: the screen is reached from an invoice the user could already open,
 * and a caller without the permission is refused by the API with a 403 that the
 * screen shows as "you do not have permission to do this" (BE-04 made that code
 * truthful). Hiding the route as well would mean someone who CAN take returns
 * but whose permission we mis-keyed cannot reach the screen at all.
 */
export const AUTHENTICATED_BUT_UNGATED = [
    // The landing page, and where RequireAuth sends anyone who lacks a
    // permission. Gating it risks a redirect loop — it is the one route that
    // cannot defend itself. For a user with NO shop the landing route becomes
    // /product/list instead; see RequireAuth.
    '/dashboard',

    // BE-19. Ungated on the server too — it only ever returns the caller's own
    // targets, so there is nothing to leak and no permission that would mean
    // anything. Every salesperson can see their own number.
    '/my/target',

    // The drill-down onto one target, and it is ungated for a REASON rather
    // than by omission: the server authorises it by ownership first, so a
    // salesperson always reaches their own, and anybody else's requires
    // `employeeTarget.overview` and comes back 403. That is a rule this map
    // cannot express — it depends on WHOSE target the id belongs to, which the
    // client does not know until it asks — so the client must not pretend to
    // enforce it. Gating the route on the overview right would lock every
    // salesperson out of their own number, which is the one thing the screen
    // exists for.
    '/my/target/:id',
]

/**
 * ⚠️ The view screens have been MOVED OUT of the list above and gated.
 *
 * The old argument was reasonable as far as it went: these are reached by
 * clicking a row in a list that is already gated, so anyone who can open the
 * list can necessarily open the row, and the API is the real boundary anyway.
 *
 * It is not what the client asked for. "If permission is not given cant see or
 * do anything" is about the screen, not only about the write. And these routes
 * are not only reached by clicking a row: a deep link, a bookmark or a pasted
 * URL arrives at them directly — and with per-shop roles, the person following
 * that link may hold nothing at all in the shop they are currently in.
 *
 * The mis-keying risk that argued against gating them is gone: the names below
 * are the backend's own, from API_REFERENCE §16.
 */
export const VIEW_SCREEN_PERMISSIONS = {
    '/purchase/order/:id': 'purchaseOrder.index',
    '/sales/order/:id': 'salesOrder.index',
    '/sales/order/return/:id/:customerId': 'salesOrder.return',
    '/customer/:id': 'customer.index',
    // ⚠️ Gated on CONVERT, not on index — and that is not an oversight about
    // "it is only a view screen". This screen's whole purpose is the button on
    // it, which moves stock out of the godown and creates customer debt.
    // Someone who may read the queue but not act on it has no reason to be
    // here, and offering them a form they cannot submit is worse than not
    // offering it. They see the queue; they do not see the till.
    '/sales/request/:draftNo': 'salesDraft.convert',
}

/**
 * What a user with NO shop may reach.
 *
 * Rule 1, 31 Jul 2026: "a user with no shop cant see any shop data, only can
 * see the product as product is for all, nothing else can be seen."
 *
 * Mirrors the server's route allowlist (`B-9a`) and `ShopPermissions::WITHOUT_SHOP`.
 * The two lists have to agree: a screen offered here but refused there is a
 * door that does not open, which the user reports as a bug.
 */
export const SHOPLESS_ROUTES = [
    '/product/list',
]
